/**
 * The session summary as an image — FR-NOTIF-5.
 *
 * WhatsApp renders a pasted image as a card in the conversation. Pasted text
 * becomes a wall of monospace that wraps at about thirty characters and loses
 * its alignment, which is why `formatForWhatsApp` avoids tables at all. An
 * image has no such constraint, so the standings can be a real table.
 *
 * Drawn on a canvas rather than rendered from DOM. The alternatives were worse:
 * `html2canvas` and friends are a dependency measured in hundreds of kilobytes
 * for a PWA that must open on a court with no signal, and SVG-to-raster through
 * an `<img>` taints the canvas on some browsers, which is exactly the sort of
 * thing that works in testing and fails on somebody's phone.
 *
 * The text summary does not go away. It stays the mechanism (FR-NOTIF-5:
 * "APIs are an accelerator, not the mechanism") — an image cannot be searched
 * in a group chat six months later, and file sharing is the first thing a
 * locked-down browser refuses.
 */

import type { PairStanding, PlayerStanding, SessionSummary } from "./api.js";
import { CARD_COLUMNS, type CardColumn, columnsThatFit, ranked, rankText } from "./statsPresentation.js";

/** The court palette, matching styles.css. Canvas has no CSS variables. */
const INK = "#f1f7f8";
const INK_DIM = "#9dbac0";
const CORK = "#e3a24e";
const COURT = "#0c333e";
const CARD = "#11454f";
const LINE = "rgba(226, 242, 244, 0.22)";

/**
 * Wider than it was, because the card now carries the same ten columns as the
 * standings screen (FR-STAT-16) rather than the five it used to.
 *
 * 1280 rather than 1240: at 1240 the ten columns needed 782px and had 776,
 * so the drop logic fired and quietly removed Dom% from every card. It was
 * working exactly as designed and the result was still wrong — a six-pixel
 * shortfall silently costing a column on every share. The fitting logic is for
 * genuinely long names, not for the ordinary case, so the ordinary case is
 * given room. Still well inside what WhatsApp re-encodes without softening the
 * digits.
 */
const WIDTH = 1280;
const PAD = 56;
const FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

/** Width reserved for the rank number and the player's name. */
const RANK_W = 52;
const NAME_W = 300;

/** How many standings rows fit before the card becomes a poster. */
const MAX_ROWS = 14;

/** Pair rows are a supporting table, so fewer of them before it gets silly. */
const MAX_PAIR_ROWS = 8;

export interface CardResult {
  /** For `navigator.share`, which takes a File and never touches the CSP. */
  blob: Blob;
  /**
   * For showing and for saving — a `data:` URL, not an object URL.
   *
   * The site's Content-Security-Policy is `img-src 'self' data:`. A `blob:`
   * URL is neither, so the browser silently refuses to decode it: the <img>
   * reports `complete: true` with `naturalWidth: 0` and the card renders as an
   * empty strip. That is exactly what shipped, and it would have looked like a
   * canvas bug forever.
   *
   * Loosening the CSP to allow `blob:` would also have worked. A data URL is
   * the smaller change — it needs no server config, it reaches phones holding
   * a cached policy, and the policy stays as tight as it was.
   */
  url: string;
  width: number;
  height: number;
}

/**
 * Renders and returns a PNG. Rejects rather than returning a broken image:
 * a canvas that fails to produce a blob is a real failure and the caller
 * falls back to text.
 */
export async function renderSummaryCard(summary: SessionSummary): Promise<CardResult> {
  const rows = summary.standings.slice(0, MAX_ROWS);
  const hiddenRows = summary.standings.length - rows.length;

  const pairRowCount = Math.min(summary.pairs.length, MAX_PAIR_ROWS);

  // Measured before drawing so the canvas is exactly as tall as its content —
  // a fixed height would either clip a big session or pad a small one.
  const layout = measure(summary, rows.length, hiddenRows > 0, pairRowCount);

  // Drawn at 2x so it stays sharp when a phone scales it up in the chat.
  const scale = 2;
  const canvas = document.createElement("canvas");
  canvas.width = WIDTH * scale;
  canvas.height = layout.height * scale;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("This browser cannot draw the image. Use the text instead.");
  ctx.scale(scale, scale);

  draw(ctx, summary, rows, hiddenRows, layout);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, "image/png");
  });
  if (!blob) throw new Error("The image could not be created. Use the text instead.");

  return { blob, url: canvas.toDataURL("image/png"), width: WIDTH, height: layout.height };
}

interface Layout {
  height: number;
  headerBottom: number;
  awardTop: number;
  awardHeight: number;
  tableTop: number;
  rowHeight: number;
  pairsTop: number;
  pairRowCount: number;
}

function measure(
  summary: SessionSummary,
  rowCount: number,
  truncated: boolean,
  pairRowCount: number,
): Layout {
  const headerBottom = PAD + 108;
  const hasAward = Boolean(summary.award && summary.award.winners.length > 0);
  const awardHeight = hasAward ? 118 : 0;
  const awardTop = headerBottom + 28;
  const tableTop = awardTop + (hasAward ? awardHeight + 34 : 0);
  const rowHeight = 62;
  const tableHeight = 54 + rowCount * rowHeight;

  // BR-PAIR-2 — the pair table is doubles-only and may legitimately be empty in
  // a session of four singles games, so it takes no height when it has no rows.
  const pairsTop = tableTop + tableHeight + (pairRowCount > 0 ? 52 : 0);
  const pairsHeight = pairRowCount > 0 ? 54 + pairRowCount * rowHeight : 0;

  // +34 for the dropped-columns note, which is cheap to reserve and worse than
  // useless to discover missing.
  const footer = 112 + (truncated ? 34 : 0) + (summary.provisional ? 34 : 0);

  return {
    height: pairsTop + pairsHeight + footer + PAD,
    headerBottom,
    awardTop,
    awardHeight,
    tableTop,
    rowHeight,
    pairsTop,
    pairRowCount,
  };
}

/**
 * Where each column sits, right-aligned, given what fits.
 *
 * Laid out from the right edge inwards so the primary column (MP) is always
 * hard against the margin — it is the number the card exists to communicate and
 * it should not move when a column is dropped.
 */
function layoutColumns(available: number): Array<CardColumn & { x: number }> {
  const cols = columnsThatFit(available);
  let x = WIDTH - PAD;
  const placed: Array<CardColumn & { x: number }> = [];
  for (const col of [...cols].reverse()) {
    placed.unshift({ ...col, x });
    x -= col.width;
  }
  return placed;
}

function draw(
  ctx: CanvasRenderingContext2D,
  summary: SessionSummary,
  rows: SessionSummary["standings"],
  hiddenRows: number,
  layout: Layout,
): void {
  ctx.fillStyle = COURT;
  ctx.fillRect(0, 0, WIDTH, layout.height);

  // ── header ────────────────────────────────────────────────────────────────
  ctx.textBaseline = "alphabetic";
  ctx.font = `700 52px ${FONT}`;
  ctx.fillStyle = INK;
  ctx.fillText("CORE ", PAD, PAD + 46);
  const coreWidth = ctx.measureText("CORE ").width;
  ctx.fillStyle = CORK;
  ctx.fillText("BADDY", PAD + coreWidth, PAD + 46);

  ctx.font = `400 26px ${FONT}`;
  ctx.fillStyle = INK_DIM;
  const when = formatDate(summary.date);
  const where = summary.venue ? ` · ${summary.venue}` : "";
  const many = `${String(summary.gameCount)} ${summary.gameCount === 1 ? "game" : "games"}`;
  ctx.fillText(`${when}${where} · ${many}`, PAD, PAD + 88);

  line(ctx, layout.headerBottom);

  // ── player of the day (BR-AWARD-2a: doubles only, so it may be absent) ────
  if (summary.award && summary.award.winners.length > 0) {
    const names = summary.award.winners.map((w) => w.displayName).join(" & ");
    const joint = summary.award.winners.length > 1 ? "Joint player of the day" : "Player of the day";

    roundRect(ctx, PAD, layout.awardTop, WIDTH - PAD * 2, layout.awardHeight, 16);
    ctx.fillStyle = CARD;
    ctx.fill();
    ctx.strokeStyle = CORK;
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.font = `600 22px ${FONT}`;
    ctx.fillStyle = INK_DIM;
    ctx.fillText(joint.toUpperCase(), PAD + 28, layout.awardTop + 40);

    ctx.font = `700 40px ${FONT}`;
    ctx.fillStyle = CORK;
    ctx.fillText(names, PAD + 28, layout.awardTop + 88);

    if (summary.award.runnersUp.length > 0) {
      const runners = summary.award.runnersUp.map((r) => r.displayName).join(" & ");
      ctx.font = `400 24px ${FONT}`;
      ctx.fillStyle = INK_DIM;
      ctx.textAlign = "right";
      ctx.fillText(`runner-up ${runners}`, WIDTH - PAD - 28, layout.awardTop + 88);
      ctx.textAlign = "left";
    }
  }

  // ── the standings table ───────────────────────────────────────────────────
  //
  // The same ten columns as the standings screen (FR-STAT-16). The card used to
  // carry five, which quietly made the richer view the private one — most of
  // the club only ever sees this image.
  //
  // MP is the primary column (FR-STAT-17): hard against the right margin,
  // heavier, in the accent, and carrying a ▼ in its header so that the emphasis
  // survives being read in greyscale.
  const nameLeft = PAD + RANK_W;
  const cols = layoutColumns(WIDTH - PAD * 2 - RANK_W - NAME_W);

  drawTableHead(ctx, cols, layout.tableTop, "");
  line(ctx, layout.tableTop + 44);

  ranked(rows).forEach((row, index) => {
    const y = layout.tableTop + 54 + index * layout.rowHeight;
    const leader = row.rank === 1;

    if (leader) {
      ctx.fillStyle = "rgba(227, 162, 78, 0.12)";
      ctx.fillRect(PAD - 16, y - 4, WIDTH - PAD * 2 + 32, layout.rowHeight - 8);
    }

    ctx.textAlign = "right";
    ctx.font = `600 24px ${FONT}`;
    ctx.fillStyle = leader ? CORK : INK_DIM;
    ctx.fillText(rankText(row.rank, row.shared), PAD + RANK_W - 16, y + 38);

    ctx.textAlign = "left";
    ctx.fillStyle = leader ? CORK : INK;
    ctx.font = `${leader ? 700 : 400} 28px ${FONT}`;
    const name = row.displayName + (row.isGuest ? " (guest)" : "");
    ctx.fillText(clip(ctx, name, NAME_W - 12), nameLeft, y + 38);

    drawRow(ctx, cols, y, leader, (key) => playerValue(row, key));

    if (index < rows.length - 1) line(ctx, y + layout.rowHeight - 8);
  });

  // ── the pair table (BR-PAIR-2, doubles only) ──────────────────────────────
  if (layout.pairRowCount > 0) {
    const pairRows = ranked(summary.pairs).slice(0, layout.pairRowCount);

    ctx.font = `600 22px ${FONT}`;
    ctx.fillStyle = INK_DIM;
    ctx.textAlign = "left";
    ctx.fillText("PARTNERSHIPS", PAD, layout.pairsTop - 12);

    drawTableHead(ctx, cols, layout.pairsTop, "");
    line(ctx, layout.pairsTop + 44);

    pairRows.forEach((pair, index) => {
      const y = layout.pairsTop + 54 + index * layout.rowHeight;
      const best = pair.rank === 1;

      ctx.textAlign = "right";
      ctx.font = `600 24px ${FONT}`;
      ctx.fillStyle = best ? CORK : INK_DIM;
      ctx.fillText(rankText(pair.rank, pair.shared), PAD + RANK_W - 16, y + 38);

      ctx.textAlign = "left";
      ctx.fillStyle = best ? CORK : INK;
      ctx.font = `${best ? 600 : 400} 26px ${FONT}`;
      ctx.fillText(clip(ctx, pair.label, NAME_W - 12), nameLeft, y + 38);

      drawRow(ctx, cols, y, best, (key) => pairValue(pair, key));

      if (index < pairRows.length - 1) line(ctx, y + layout.rowHeight - 8);
    });
  }

  // ── footer ────────────────────────────────────────────────────────────────
  let y =
    (layout.pairRowCount > 0
      ? layout.pairsTop + 54 + layout.pairRowCount * layout.rowHeight
      : layout.tableTop + 54 + rows.length * layout.rowHeight) + 46;
  ctx.font = `400 22px ${FONT}`;
  ctx.fillStyle = INK_DIM;

  if (hiddenRows > 0) {
    ctx.fillText(
      `+ ${String(hiddenRows)} more ${hiddenRows === 1 ? "player" : "players"} — full table in the app`,
      PAD,
      y,
    );
    y += 34;
  }

  // BR-COMMIT-2 — an uncommitted session's figures can still change, and a
  // picture in a group chat outlives the session it came from.
  if (summary.provisional) {
    ctx.fillStyle = CORK;
    ctx.fillText("Provisional — the session has not been committed yet", PAD, y);
    y += 34;
  }

  // FR-STAT-16 — "never silently". If the fit dropped anything, the card says
  // which, so a reader looking for a column knows it was omitted rather than
  // wondering whether the app has stopped computing it.
  const dropped = CARD_COLUMNS.filter((c) => !cols.some((k) => k.key === c.key));
  if (dropped.length > 0) {
    ctx.fillStyle = INK_DIM;
    ctx.fillText(
      `${dropped.map((c) => c.label).join(", ")} omitted for width — full table in the app`,
      PAD,
      y,
    );
    y += 34;
  }

  ctx.fillStyle = INK_DIM;
  ctx.fillText("MP = wins + bonus − penalties", PAD, y);
}

/** Column headers, with the primary one marked so greyscale still shows it. */
function drawTableHead(
  ctx: CanvasRenderingContext2D,
  cols: Array<CardColumn & { x: number }>,
  top: number,
  rankLabel: string,
): void {
  ctx.font = `600 22px ${FONT}`;
  ctx.textAlign = "right";
  ctx.fillStyle = INK_DIM;
  ctx.fillText(rankLabel, PAD + RANK_W - 16, top + 26);

  for (const col of cols) {
    const primary = col.key === "matchPoints";
    ctx.fillStyle = primary ? CORK : INK_DIM;
    ctx.font = `${primary ? 700 : 600} 22px ${FONT}`;
    ctx.fillText(primary ? `${col.label} ▼` : col.label, col.x, top + 26);
  }
  ctx.textAlign = "left";
}

/** One row of figures, in the columns that survived the fit. */
function drawRow(
  ctx: CanvasRenderingContext2D,
  cols: Array<CardColumn & { x: number }>,
  y: number,
  highlighted: boolean,
  valueOf: (key: CardColumn["key"]) => string,
): void {
  ctx.textAlign = "right";
  for (const col of cols) {
    const primary = col.key === "matchPoints";
    if (primary) {
      ctx.font = `700 32px ${FONT}`;
      ctx.fillStyle = highlighted ? CORK : INK;
    } else {
      ctx.font = `${highlighted ? 600 : 400} 26px ${FONT}`;
      ctx.fillStyle = highlighted ? CORK : INK_DIM;
    }
    ctx.fillText(valueOf(col.key), col.x, y + 38);
  }
  ctx.textAlign = "left";
}

/**
 * BR-STAT-1 — an em dash where there is nothing to divide by, never a zero.
 * Percentages lose their decimal here: "47%" is what the eye reads off a phone
 * screenshot, and "46.7%" costs twenty pixels to say the same thing.
 */
function num(value: number | null, kind: "int" | "pct" | "dec"): string {
  if (value === null) return "—";
  if (kind === "pct") return `${Math.round(value * 100)}%`;
  if (kind === "dec") return value.toFixed(1);
  return String(value);
}

function playerValue(row: PlayerStanding, key: CardColumn["key"]): string {
  switch (key) {
    case "games": return num(row.games, "int");
    case "wins": return num(row.wins, "int");
    case "winPct": return num(row.winPct, "pct");
    case "pointsWon": return num(row.pointsWon, "int");
    case "pointsLost": return num(row.pointsLost, "int");
    case "dominance": return num(row.dominance, "dec");
    case "normalisedDominance": return num(row.normalisedDominance, "pct");
    case "bonus": return num(row.bonus, "int");
    case "penalties": return num(row.penalties, "int");
    case "matchPoints": return num(row.matchPoints, "int");
  }
}

function pairValue(row: PairStanding, key: CardColumn["key"]): string {
  switch (key) {
    case "games": return num(row.games, "int");
    case "wins": return num(row.wins, "int");
    case "winPct": return num(row.winPct, "pct");
    case "pointsWon": return num(row.pointsWon, "int");
    case "pointsLost": return num(row.pointsLost, "int");
    case "dominance": return num(row.dominance, "dec");
    case "normalisedDominance": return num(row.normalisedDominance, "pct");
    case "bonus": return num(row.bonus, "int");
    case "penalties": return num(row.penalties, "int");
    case "matchPoints": return num(row.matchPoints, "int");
  }
}

function line(ctx: CanvasRenderingContext2D, y: number): void {
  ctx.strokeStyle = LINE;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(PAD, y + 0.5);
  ctx.lineTo(WIDTH - PAD, y + 0.5);
  ctx.stroke();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Truncates to a pixel width rather than a character count — "Wu" and
    "Mmmmm" are not the same width and a guest's club name can be long. */
function clip(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let cut = text;
  while (cut.length > 1 && ctx.measureText(`${cut}…`).width > maxWidth) {
    cut = cut.slice(0, -1);
  }
  return `${cut}…`;
}

/**
 * The date as the club says it. Parsed with an explicit midnight so it reads in
 * the browser's own zone — a bare `new Date("2026-08-01")` is parsed as UTC and
 * renders as 31 July for everybody east of Greenwich, which is the same defect
 * that was once shifting session dates on the server.
 */
function formatDate(date: string): string {
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

/**
 * True when this browser can hand a file to the share sheet. Checked with the
 * actual file, because `navigator.share` existing says nothing about whether
 * files are accepted — iOS Safari and Android Chrome differ, and Firefox
 * accepts neither.
 */
export function canShareFile(file: File): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] })
  );
}
